import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";

export default function Header({ isDarkMode, toggleDarkMode }) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [authTick, setAuthTick] = useState(0);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const onAuth = () => setAuthTick((t) => t + 1);
    window.addEventListener("cliptranscript-auth-changed", onAuth);
    return () => window.removeEventListener("cliptranscript-auth-changed", onAuth);
  }, []);

  void authTick;
  const isLoggedIn = localStorage.getItem("token");
  const userEmail = localStorage.getItem("userEmail");
  const userName = userEmail ? userEmail.split("@")[0] : "User";

  // Close mobile menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        showMobileMenu &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setShowMobileMenu(false);
      }
    }
    if (showMobileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showMobileMenu]);

  // Close on Esc
  useEffect(() => {
    function handleKeyDown(e) {
      if (showMobileMenu && e.key === "Escape") setShowMobileMenu(false);
    }
    if (showMobileMenu) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showMobileMenu]);

  const colors = useMemo(
    () => ({
      bg: isDarkMode ? "#1a1a1a" : "#ffffff",
      border: isDarkMode ? "#333" : "#e0e0e0",
      text: isDarkMode ? "#b0b0b0" : "#666",
      textDark: isDarkMode ? "#ffffff" : "#000000",
      shadow: isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)",
      hoverBg: isDarkMode ? "#3a3a3a" : "#f5f5f5",
      menuBg: isDarkMode ? "#2a2a2a" : "#ffffff"
    }),
    [isDarkMode]
  );

  const headerStyle = {
    backgroundColor: colors.bg,
    borderBottom: `1px solid ${colors.border}`,
    padding: "clamp(12px, 3vw, 15px) clamp(15px, 4vw, 175px)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: `0 2px 8px ${colors.shadow}`,
    position: "relative"
  };

  const containerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    gap: "clamp(20px, 3vw, 30px)"
  };

  const logoStyle = {
    fontSize: "clamp(18px, 4vw, 24px)",
    fontWeight: "bold",
    color: colors.textDark,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "clamp(8px, 2vw, 10px)",
    whiteSpace: "nowrap"
  };

  const centerNavStyle = {
    display: "flex",
    gap: "clamp(20px, 3vw, 30px)",
    alignItems: "center",
    flex: 1,
    justifyContent: "center"
  };

  const rightNavStyle = {
    display: "flex",
    gap: "clamp(15px, 2vw, 30px)",
    alignItems: "center"
  };

  const linkStyle = {
    color: colors.text,
    textDecoration: "none",
    cursor: "pointer",
    fontSize: "clamp(12px, 2vw, 14px)",
    transition: "color 0.3s",
    whiteSpace: "nowrap"
  };

  const buttonStyle = {
    padding: "clamp(8px, 2vw, 12px) clamp(12px, 2vw, 16px)",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "clamp(12px, 2vw, 14px)",
    transition: "all 0.3s",
    whiteSpace: "nowrap"
  };

  const darkModeButtonStyle = {
    padding: "clamp(6px, 2vw, 8px) clamp(8px, 2vw, 12px)",
    backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
    color: isDarkMode ? "#ffc107" : "#333",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "clamp(16px, 3vw, 18px)",
    transition: "all 0.3s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const hamburgerStyle = {
    display: "none",
    flexDirection: "column",
    gap: "5px",
    cursor: "pointer",
    padding: "8px",
    backgroundColor: "transparent",
    border: "none",
    fontSize: "24px",
    color: colors.textDark,
    transition: "all 0.3s",
    "@media (max-width: 768px)": {
      display: "flex"
    }
  };

  const mobileMenuStyle = {
    position: "absolute",
    top: "100%",
    left: "0",
    right: "0",
    backgroundColor: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderTop: "none",
    boxShadow: `0 4px 12px ${colors.shadow}`,
    zIndex: 999,
    display: "flex",
    flexDirection: "column",
    gap: "0",
    animation: "slideDown 0.3s ease-out",
    "@keyframes slideDown": {
      from: { opacity: 0, transform: "translateY(-10px)" },
      to: { opacity: 1, transform: "translateY(0)" }
    }
  };

  const mobileMenuItemStyle = {
    padding: "clamp(12px, 3vw, 14px) clamp(16px, 4vw, 20px)",
    color: colors.text,
    cursor: "pointer",
    borderBottom: `1px solid ${colors.border}`,
    fontSize: "clamp(12px, 2vw, 14px)",
    transition: "background-color 0.2s"
  };

  const userAvatarStyle = {
    width: "clamp(32px, 5vw, 36px)",
    height: "clamp(32px, 5vw, 36px)",
    borderRadius: "50%",
    backgroundColor: "#6f42c1",
    color: "white",
    border: "2px solid #6f42c1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "clamp(14px, 2vw, 16px)",
    fontWeight: "bold",
    transition: "transform 0.2s",
    position: "relative"
  };

  const userMenuStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: "0",
    backgroundColor: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    boxShadow: `0 4px 12px ${colors.shadow}`,
    zIndex: 1000,
    minWidth: "180px",
    overflow: "hidden"
  };

  const menuItemStyle = {
    padding: "clamp(10px, 2vw, 12px) clamp(14px, 2vw, 16px)",
    color: colors.text,
    cursor: "pointer",
    borderBottom: `1px solid ${colors.border}`,
    fontSize: "clamp(12px, 2vw, 14px)",
    transition: "background-color 0.2s"
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    setShowUserMenu(false);
    setShowMobileMenu(false);
    navigate("/");
  }, [navigate]);

  const handleMobileNavClick = useCallback((path) => {
    navigate(path);
    setShowMobileMenu(false);
  }, [navigate]);

  // Inject keyframes
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @media (max-width: 768px) {
        [data-nav="centerNav"] { display: none !important; }
        [data-nav="desktop-buttons"] { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  return (
    <header style={headerStyle}>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
          [data-nav="centerNav"] { display: none !important; }
          [data-nav="desktop-buttons"] { display: none !important; }
        }
      `}</style>

      <div style={containerStyle}>
        {/* Logo - Always visible */}
        <div style={logoStyle} onClick={() => {
          navigate("/");
          setShowMobileMenu(false);
        }}>
          <img src="/images/logo.png" alt="ClipTranscript" style={{ height: "clamp(24px, 5vw, 32px)", width: "auto" }} />
          <span style={{ display: "clamp(0, 0, 100%) !== 0 ? inline : none" }}>ClipTranscript</span>
        </div>

        {/* Center Nav - Desktop only */}
        <nav style={centerNavStyle} data-nav="centerNav">
          {isLoggedIn ? (
            <span
              style={linkStyle}
              onClick={() => navigate("/dashboard")}
              onMouseEnter={(e) => e.target.style.color = "#007bff"}
              onMouseLeave={(e) => e.target.style.color = colors.text}
            >
              Dashboard
            </span>
          ) : (
            <>
              <a
                href="#features"
                style={linkStyle}
                onMouseEnter={(e) => e.target.style.color = "#007bff"}
                onMouseLeave={(e) => e.target.style.color = colors.text}
              >
                Features
              </a>
              <a
                href="#faq"
                style={linkStyle}
                onMouseEnter={(e) => e.target.style.color = "#007bff"}
                onMouseLeave={(e) => e.target.style.color = colors.text}
              >
                FAQ
              </a>
            </>
          )}
        </nav>

        {/* Right Nav */}
        <nav style={rightNavStyle}>
          <button
            onClick={toggleDarkMode}
            style={darkModeButtonStyle}
            title={isDarkMode ? "Light Mode" : "Dark Mode"}
            onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
            onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
          >
            {isDarkMode ? "☀️" : "🌙"}
          </button>

          {/* Hamburger Menu - Mobile only */}
          <button
            ref={buttonRef}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            style={{
              ...hamburgerStyle,
              display: window.innerWidth <= 768 ? "flex" : "none",
              color: colors.textDark
            }}
            title="Menu"
            aria-label="Toggle menu"
            aria-expanded={showMobileMenu}
          >
            ☰
          </button>

          {/* Desktop Auth Buttons */}
          <div style={{ display: "flex", gap: "clamp(10px, 2vw, 15px)" }} data-nav="desktop-buttons">
            {isLoggedIn ? (
              <div style={{ position: "relative" }}>
                <div
                  style={userAvatarStyle}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  title={userEmail}
                  onMouseEnter={(e) => e.target.style.transform = "scale(1.05)"}
                  onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                >
                  {userName.charAt(0).toUpperCase()}
                </div>
                
                {showUserMenu && (
                  <div style={userMenuStyle}>
                    <div style={{ ...menuItemStyle, borderBottom: "none", fontWeight: "bold", color: "#6f42c1" }}>
                      {userEmail}
                    </div>
                    <div
                      style={menuItemStyle}
                      onClick={() => {
                        navigate("/dashboard");
                        setShowUserMenu(false);
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      📊 Dashboard
                    </div>
                    <div
                      style={{ ...menuItemStyle, borderBottom: "none" }}
                      onClick={handleLogout}
                      onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      🚪 Logout
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button 
                  style={buttonStyle} 
                  onClick={() => navigate("/signin")}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "#007bff"}
                >
                  Sign In
                </button>
                <button
                  style={{ ...buttonStyle, backgroundColor: "#28a745" }}
                  onClick={() => navigate("/signup")}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "#218838"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "#28a745"}
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div style={mobileMenuStyle} ref={menuRef}>
          {isLoggedIn ? (
            <div
              style={mobileMenuItemStyle}
              onClick={() => handleMobileNavClick("/dashboard")}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              📊 Dashboard
            </div>
          ) : (
            <>
              <a
                href="#features"
                style={{ ...mobileMenuItemStyle, textDecoration: "none" }}
                onClick={() => setShowMobileMenu(false)}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                ✨ Features
              </a>
              <a
                href="#faq"
                style={{ ...mobileMenuItemStyle, textDecoration: "none" }}
                onClick={() => setShowMobileMenu(false)}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                ❓ FAQ
              </a>
            </>
          )}

          {isLoggedIn ? (
            <>
              <div
                style={{ ...mobileMenuItemStyle, fontWeight: "bold", color: "#6f42c1" }}
              >
                {userEmail}
              </div>
              <div
                style={mobileMenuItemStyle}
                onClick={() => handleMobileNavClick("/dashboard")}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                📊 Dashboard
              </div>
              <div
                style={{ ...mobileMenuItemStyle, borderBottom: "none" }}
                onClick={handleLogout}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                🚪 Logout
              </div>
            </>
          ) : (
            <>
              <button
                style={{ ...mobileMenuItemStyle, backgroundColor: "transparent", textAlign: "left", width: "100%" }}
                onClick={() => handleMobileNavClick("/signin")}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                🔐 Sign In
              </button>
              <button
                style={{ ...mobileMenuItemStyle, backgroundColor: "transparent", textAlign: "left", width: "100%", borderBottom: "none" }}
                onClick={() => handleMobileNavClick("/signup")}
                onMouseEnter={(e) => e.target.style.backgroundColor = colors.hoverBg}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                🚀 Get Started
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
}